import React from 'react';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { Box, styled } from '@mui/material';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplaySwitch } from 'components/settings/SettingDisplaySwitch';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { SettingDisplay } from 'lib/SettingDisplay';

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

interface IProps {
    settings: IPublicAndPrivateSettings;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
    internalSettingsEditable: boolean;
    internalSettingsVisible: boolean;
}

// Subscriptions are meant to be globally managed only, location settings are **not** supported due to the nature of the subscription work.
export function SubscriptionSettingsCard({
    settings,
    onSubmit,
    internalSettingsVisible,
    internalSettingsEditable
}: IProps) {
    return (
        <StyledMuiCardForm
            title="Subscriptions"
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
            initialValues={{
                subscriptionsEnabled: settings?.subscriptionsEnabled ?? false,
                subscriptionPaymentReceivedEmailEnabled:
                    settings?.actionNotifications?.SUBSCRIPTION_PAYMENT_RECEIVED?.email ?? false,
                subscriptionCancellationRequestedEmailEnabled:
                    settings?.actionNotifications?.SUBSCRIPTION_CANCELLATION_REQUESTED?.email ?? false,
                subscriptionCancelledEmailEnabled:
                    settings?.actionNotifications?.SUBSCRIPTION_CANCELLED?.email ?? false
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    {internalSettingsVisible && (
                        <SettingDisplaySwitch
                            title="Subscriptions Enabled"
                            checked={!!settings?.subscriptionsEnabled}
                        />
                    )}
                    <SettingDisplaySwitch
                        title='Enable Email for "Payment Received" Actions'
                        checked={!!settings?.actionNotifications?.SUBSCRIPTION_PAYMENT_RECEIVED?.email}
                    />
                    <SettingDisplaySwitch
                        title='Enable Email for "Cancellation Requested" Actions'
                        checked={!!settings?.actionNotifications?.SUBSCRIPTION_CANCELLATION_REQUESTED?.email}
                    />
                    <SettingDisplaySwitch
                        title='Enable Email for "Subscription Cancelled" Actions'
                        checked={!!settings?.actionNotifications?.SUBSCRIPTION_CANCELLED?.email}
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    {internalSettingsEditable && (
                        <SettingDisplay label="Subscriptions Enabled">
                            <SwitchFormField name="subscriptionsEnabled" />
                        </SettingDisplay>
                    )}
                    <SettingDisplay label='Enable Email for "Payment Received" Actions'>
                        <SwitchFormField name="subscriptionPaymentReceivedEmailEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label='Enable Email for "Cancellation Requested" Actions'>
                        <SwitchFormField name="subscriptionCancellationRequestedEmailEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label='Enable Email for "Subscription Cancelled" Actions'>
                        <SwitchFormField name="subscriptionCancelledEmailEnabled" />
                    </SettingDisplay>
                </Box>
            }
        />
    );
}
